import { useEffect, useState } from 'react';
import Card from '../ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { useBranches } from '../../hooks/useBranches';
import {
  deleteBranch,
  migrateBatchesToBranchIds,
  seedDefaultBranches,
  upsertBranch,
} from '../../utils/settingsMutations';
import BranchForm from './BranchForm';

const BranchesSettings = () => {
  const { currentUser } = useAuth();
  const { list, map, loading } = useBranches();

  const [formOpen, setFormOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (list.length > 0) return;
    (async () => {
      try {
        await seedDefaultBranches({ operator: currentUser });
      } catch (err) {
        console.warn('Auto-seed branches failed:', err);
      }
    })();
  }, [loading, list.length, currentUser]);

  const flash = (type, text) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 4000);
  };

  const openCreate = () => {
    setEditingBranch(null);
    setFormOpen(true);
  };

  const openEdit = (branch) => {
    setEditingBranch(branch);
    setFormOpen(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await upsertBranch({
        branchId: editingBranch?.id || null,
        values: {
          ...values,
          order: editingBranch?.order ?? list.length,
        },
        operator: currentUser,
      });
      flash('success', editingBranch ? 'Branch updated.' : 'Branch added.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (branch) => {
    const confirmed = window.confirm(
      `Delete "${branch.name}"? This cannot be undone. Existing batches assigned to this branch will keep their name but lose the link.`,
    );
    if (!confirmed) return;
    try {
      await deleteBranch({ branchId: branch.id });
      flash('success', `Deleted ${branch.name}.`);
    } catch (err) {
      flash('error', err?.message || 'Failed to delete branch.');
    }
  };

  const handleToggleActive = async (branch) => {
    try {
      await upsertBranch({
        branchId: branch.id,
        values: { ...branch, isActive: !branch.isActive },
        operator: currentUser,
      });
      flash('success', branch.isActive ? 'Branch hidden.' : 'Branch activated.');
    } catch (err) {
      flash('error', err?.message || 'Failed to update branch.');
    }
  };

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const result = await migrateBatchesToBranchIds({ branches: map });
      flash(
        'success',
        `Migrated ${result.updatedBatches} of ${result.scannedBatches} batches to branch IDs.`,
      );
    } catch (err) {
      flash('error', err?.message || 'Migration failed.');
    } finally {
      setMigrating(false);
    }
  };

  return (
    <Card className="shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">Branches</h2>
          <p className="text-sm text-stone-500">
            Add every physical location. Active branches populate the batch creation
            form automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleMigrate}
            disabled={migrating || loading || list.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-60"
            title="Re-tag existing batches with branch IDs after adding new branches."
          >
            {migrating ? 'Migrating...' : 'Migrate existing batches'}
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-700"
          >
            Add branch
          </button>
        </div>
      </div>

      {message ? (
        <div
          className={`mt-4 rounded-md px-3 py-2 text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
              : 'bg-red-50 text-red-700 ring-1 ring-red-200'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        {loading ? (
          <p className="text-sm text-stone-500">Loading branches...</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-stone-500">
            No branches yet. They will be seeded automatically.
          </p>
        ) : (
          list.map((branch) => (
            <div
              key={branch.id}
              className="flex flex-col gap-3 rounded-lg border border-stone-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-base font-semibold text-stone-900">
                    {branch.name}
                  </h3>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      branch.isActive
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-stone-100 text-stone-600 ring-1 ring-stone-200'
                    }`}
                  >
                    {branch.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-stone-500">
                  Slug: {branch.slug || '—'}
                  {branch.city ? ` · ${branch.city}` : ''}
                </p>
                {branch.addressLine ? (
                  <p className="mt-1 text-xs text-stone-500">{branch.addressLine}</p>
                ) : null}
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-stone-500">
                  {branch.contactPhone ? <span>{branch.contactPhone}</span> : null}
                  {branch.contactEmail ? <span>{branch.contactEmail}</span> : null}
                  {branch.googleMapsUrl ? (
                    <a
                      href={branch.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-600 hover:text-pink-700"
                    >
                      Open map
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleToggleActive(branch)}
                  className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
                >
                  {branch.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(branch)}
                  className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(branch)}
                  className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <BranchForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        branch={editingBranch}
        submitting={submitting}
      />
    </Card>
  );
};

export default BranchesSettings;
